import React, { useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DownloadSimple } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_COLORS = [
  { max: 39, color: "#ff3b30", label: "Critica" },
  { max: 59, color: "#ff9500", label: "Potenziale" },
  { max: 74, color: "#ffcc00", label: "Promettente" },
  { max: 89, color: "#34c759", label: "Solida" },
  { max: 100, color: "#0071e3", label: "Dominante" },
];
const bandColor = (v) => BAND_COLORS.find((b) => v <= b.max)?.color || "#0071e3";

const PARTICIPANT_COLORS = [
  "#1B4FD8", "#E11D48", "#10B981", "#F59E0B", "#8B5CF6",
  "#06B6D4", "#EC4899", "#84CC16", "#F97316", "#6366F1",
];

// ── Radar chart (SVG) ────────────────────────────────────────────
function RadarChart({ participants, areaLabels, areaKeys, teamAvg }) {
  const size = 480;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 170;
  const n = areaLabels.length;

  const pointAt = (idx, value) => {
    const angle = (Math.PI * 2 * idx) / n - Math.PI / 2;
    const r = (radius * value) / 100;
    return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
  };

  const gridLevels = [20, 40, 60, 80, 100];
  const axisEnd = (idx) => pointAt(idx, 100);
  const labelPos = (idx) => {
    const angle = (Math.PI * 2 * idx) / n - Math.PI / 2;
    const r = radius + 22;
    return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
  };

  const polygonForScores = (scores) => {
    // B73: quantizza al midpoint fascia (20/50/67/82/95), no valori esatti.
    const midOf = (s) => (s >= 90 ? 95 : s >= 75 ? 82 : s >= 60 ? 67 : s >= 40 ? 50 : 20);
    const readMid = (entry) => {
      if (!entry) return 0;
      if (typeof entry.band_midpoint === "number") return entry.band_midpoint;
      const s = typeof entry.score === "number" ? entry.score : 0;
      return midOf(s);
    };
    const vec = areaKeys.map((k) => readMid(scores?.areas?.[k]));
    return vec.map((v, i) => pointAt(i, v).join(",")).join(" ");
  };

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[480px] mx-auto" data-testid="sim-radar-chart">
      {/* grid circles */}
      {gridLevels.map((lvl) => (
        <polygon
          key={lvl}
          points={areaLabels.map((_, i) => pointAt(i, lvl).join(",")).join(" ")}
          fill="none"
          stroke="#0D1F3C"
          strokeOpacity={0.08}
        />
      ))}
      {/* axes */}
      {areaLabels.map((_, i) => {
        const [ex, ey] = axisEnd(i);
        return <line key={i} x1={cx} y1={cy} x2={ex} y2={ey} stroke="#0D1F3C" strokeOpacity={0.08} />;
      })}
      {/* team avg */}
      {teamAvg && (
        <polygon
          points={teamAvg.map((v, i) => pointAt(i, v).join(",")).join(" ")}
          fill="#0D1F3C"
          fillOpacity={0.06}
          stroke="#0D1F3C"
          strokeDasharray="4 3"
          strokeWidth={1.5}
        />
      )}
      {/* participants */}
      {participants.map((p, pi) => {
        const color = PARTICIPANT_COLORS[pi % PARTICIPANT_COLORS.length];
        return (
          <polygon
            key={pi}
            points={polygonForScores(p.scores)}
            fill={color}
            fillOpacity={0.12}
            stroke={color}
            strokeWidth={2}
          />
        );
      })}
      {/* labels */}
      {areaLabels.map((lbl, i) => {
        const [lx, ly] = labelPos(i);
        return (
          <text
            key={i}
            x={lx}
            y={ly}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="11"
            fill="#0D1F3C"
            fontWeight="500"
          >
            {lbl}
          </text>
        );
      })}
    </svg>
  );
}

// ── Heatmap similarity ────────────────────────────────────────────
function SimilarityHeatmap({ participants, matrix }) {
  const n = participants.length;
  const cell = 64;
  const pad = 140;
  const width = pad + cell * n + 20;
  const height = pad + cell * n + 20;

  const simAt = (i, j) => {
    if (i === j) return 100;
    const key = matrix.find(
      (m) => (m.a_idx === i && m.b_idx === j) || (m.a_idx === j && m.b_idx === i)
    );
    return key ? key.similarity : 0;
  };

  const colorForSim = (v) => {
    // gradient red→yellow→green
    if (v >= 80) return "#22c55e";
    if (v >= 65) return "#84cc16";
    if (v >= 50) return "#facc15";
    if (v >= 35) return "#f97316";
    return "#ef4444";
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-[640px] mx-auto" data-testid="sim-heatmap">
      {/* col headers */}
      {participants.map((p, j) => (
        <text
          key={`c-${j}`}
          x={pad + cell * j + cell / 2}
          y={pad - 10}
          textAnchor="middle"
          fontSize="11"
          fill="#0D1F3C"
          fontWeight="500"
        >
          P{j + 1}
        </text>
      ))}
      {/* rows */}
      {participants.map((p, i) => (
        <g key={`r-${i}`}>
          <text
            x={pad - 10}
            y={pad + cell * i + cell / 2}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize="11"
            fill="#0D1F3C"
            fontWeight="500"
          >
            P{i + 1} · {p.name.split(" ")[0]}
          </text>
          {participants.map((_, j) => {
            const v = simAt(i, j);
            return (
              <g key={`${i}-${j}`}>
                <rect
                  x={pad + cell * j}
                  y={pad + cell * i}
                  width={cell - 2}
                  height={cell - 2}
                  fill={i === j ? "#E5E7EB" : colorForSim(v)}
                  rx={6}
                />
                <text
                  x={pad + cell * j + cell / 2}
                  y={pad + cell * i + cell / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="12"
                  fill={i === j ? "#9CA3AF" : "white"}
                  fontWeight="600"
                >
                  {i === j ? "—" : `${v}%`}
                </text>
              </g>
            );
          })}
        </g>
      ))}
    </svg>
  );
}

// ── Relational diagram (force layout statico) ─────────────────────
function RelationalDiagram({ participants, matrix }) {
  const size = 420;
  const cx = size / 2;
  const cy = size / 2;
  const r = 150;
  const n = participants.length;

  const nodePos = (i) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
  };

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[420px] mx-auto" data-testid="sim-relational-diagram">
      {/* edges */}
      {matrix.map((m, k) => {
        const [x1, y1] = nodePos(m.a_idx);
        const [x2, y2] = nodePos(m.b_idx);
        const thickness = Math.max(1, (m.similarity / 100) * 6);
        const opacity = 0.25 + (m.similarity / 100) * 0.55;
        return (
          <line
            key={k}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="#1B4FD8"
            strokeWidth={thickness}
            strokeOpacity={opacity}
          />
        );
      })}
      {/* nodes */}
      {participants.map((p, i) => {
        const [x, y] = nodePos(i);
        const color = PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length];
        const initials = (p.name.split(" ").map((s) => s[0]).join("").slice(0, 2) || "?").toUpperCase();
        return (
          <g key={i}>
            <circle cx={x} cy={y} r={26} fill={color} />
            <text
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="14"
              fill="white"
              fontWeight="700"
            >
              {initials}
            </text>
            <text
              x={x}
              y={y + 44}
              textAnchor="middle"
              fontSize="11"
              fill="#0D1F3C"
              fontWeight="600"
            >
              {p.name.split(" ")[0]}
            </text>
            <text
              x={x}
              y={y + 58}
              textAnchor="middle"
              fontSize="10"
              fill="#0D1F3C"
              fillOpacity={0.6}
            >
              {p.sim_role}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Main Report ────────────────────────────────────────────────────
export default function SimulationReport({ sim }) {
  const participants = sim?.participants_snapshot || [];
  const matrix = sim?.similarity_matrix || [];
  const areaLabels = sim?.area_labels || ["Iniziativa","Affidabilita","Metodo","Qualita","Focus","Analisi","Creativita","Relazione","Influenza","Integrita"];
  const areaKeys = sim?.area_keys || ["INI","AFF","MET","QUA","FOC","ANA","CRE","REL","INF","INT"];
  const teamAvg = sim?.team_avg_vector || [];
  const avgSim = sim?.avg_similarity || 0;
  const dispersion = sim?.dispersion_by_area || {};

  const overallBand = useMemo(() => {
    // B73: usa i band_midpoint dei partecipanti se disponibili, sennò calcola dai .score.
    const midOf = (s) => (s >= 90 ? 95 : s >= 75 ? 82 : s >= 60 ? 67 : s >= 40 ? 50 : 20);
    const readMid = (entry) => {
      if (!entry) return 0;
      if (typeof entry.band_midpoint === "number") return entry.band_midpoint;
      const s = typeof entry.score === "number" ? entry.score : 0;
      return midOf(s);
    };
    const avg =
      participants.length > 0
        ? participants.reduce((acc, p) => {
            const vec = areaKeys.map((k) => readMid(p.scores?.areas?.[k]));
            return acc + vec.reduce((a, b) => a + b, 0) / vec.length;
          }, 0) / participants.length
        : 0;
    // Etichetta band basata sul midpoint medio
    let label = "Critica"; let color = "#ff3b30";
    if (avg >= 90) { label = "Dominante"; color = "#0071e3"; }
    else if (avg >= 75) { label = "Solida"; color = "#34c759"; }
    else if (avg >= 60) { label = "Promettente"; color = "#ffcc00"; }
    else if (avg >= 40) { label = "Potenziale"; color = "#ff9500"; }
    return { label, color };
  }, [participants, areaKeys]);

  const downloadPdf = async () => {
    if (!sim?.id) return;
    try {
      const resp = await axios.get(`${API}/simulations/${sim.id}/pdf`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `simulazione_${(sim.name || "team").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore download PDF");
    }
  };

  if (!sim) return null;

  const isLegacy = !sim.similarity_matrix || sim.similarity_matrix.length === 0 || participants.length === 0;

  if (isLegacy) {
    return (
      <div className="space-y-6" data-testid="simulation-report">
        <section>
          <h1 className="text-2xl font-semibold text-[#0D1F3C]">{sim.name}</h1>
          <p className="text-sm text-[#0D1F3C]/60 mt-1">
            {sim.sim_type}
            {sim.position_title ? ` · ${sim.position_title}` : ""}
          </p>
          <p className="text-xs text-[#0D1F3C]/40 mt-0.5">
            Generato il {new Date(sim.created_at).toLocaleDateString("it-IT")}
          </p>
        </section>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-800">
            Questa simulazione e in formato legacy: i dati comparativi (radar, matrice di similarita, grafo relazionale) non sono disponibili. Puoi comunque consultare il report sottostante.
          </CardContent>
        </Card>
        {sim.report_html && (
          <section
            className="prose prose-sm max-w-none [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-[#0D1F3C] [&_h2]:mt-6 [&_h2]:mb-3 [&_p]:text-[#0D1F3C]/80 [&_p]:leading-relaxed [&_ul]:text-[#0D1F3C]/80"
            dangerouslySetInnerHTML={{ __html: sim.report_html }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="simulation-report">
      {/* 1. Intestazione */}
      <section data-testid="sim-section-header">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-[#0D1F3C]">{sim.name}</h1>
            <p className="text-sm text-[#0D1F3C]/60 mt-1">
              {sim.sim_type}
              {sim.position_title ? ` · ${sim.position_title}` : ""}
              {sim.position_department ? ` (${sim.position_department})` : ""}
            </p>
            <p className="text-xs text-[#0D1F3C]/40 mt-0.5">
              Generato il {new Date(sim.created_at).toLocaleDateString("it-IT")}
            </p>
          </div>
          <div className="flex gap-3 items-start">
            <Button
              variant="outline"
              size="sm"
              onClick={downloadPdf}
              data-testid="sim-download-pdf-btn"
              className="border-[#1B4FD8]/30 text-[#1B4FD8] hover:bg-[#1B4FD8]/5"
            >
              <DownloadSimple size={14} className="mr-1.5" /> PDF
            </Button>
            <div className="bg-white border border-[#0D1F3C]/8 rounded-lg px-4 py-3 text-center">
              <div className="text-xs text-[#0D1F3C]/50 uppercase tracking-wide">Similarita media</div>
              <div className="text-2xl font-bold text-[#1B4FD8]">{avgSim}%</div>
            </div>
            <div className="bg-white border border-[#0D1F3C]/8 rounded-lg px-4 py-3 text-center">
              <div className="text-xs text-[#0D1F3C]/50 uppercase tracking-wide">Profilo team</div>
              <div className="text-xl font-bold" style={{ color: overallBand.color }}>
                {overallBand.label}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Partecipanti */}
      <section data-testid="sim-section-participants">
        <h2 className="text-lg font-semibold text-[#0D1F3C] mb-4">Partecipanti ({participants.length})</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {participants.map((p, i) => {
            const color = PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length];
            const initials = (p.name.split(" ").map((s) => s[0]).join("").slice(0, 2) || "?").toUpperCase();
            return (
              <Card key={i} className="border-[#0D1F3C]/8">
                <CardContent className="p-4 flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                    style={{ backgroundColor: color }}
                  >
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#0D1F3C] truncate">{p.name}</div>
                    <div className="text-xs text-[#0D1F3C]/60 truncate">{p.role || "—"}</div>
                    <Badge
                      className="mt-1 text-[10px]"
                      variant="outline"
                    >
                      {p.sim_role}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* 3. Radar comparativo */}
      <section data-testid="sim-section-radar">
        <h2 className="text-lg font-semibold text-[#0D1F3C] mb-4">Profilo Comportamentale Comparato</h2>
        <Card className="border-[#0D1F3C]/8">
          <CardContent className="p-6">
            <RadarChart
              participants={participants}
              areaLabels={areaLabels}
              areaKeys={areaKeys}
              teamAvg={teamAvg}
            />
            <div className="flex flex-wrap gap-3 justify-center mt-4">
              {participants.map((p, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs">
                  <span
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length] }}
                  />
                  <span className="text-[#0D1F3C]">{p.name.split(" ")[0]}</span>
                </div>
              ))}
              <div className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded-sm border border-dashed border-[#0D1F3C]/60" />
                <span className="text-[#0D1F3C]/60">Media team</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 4. Heatmap + Diagramma relazionale */}
      <section data-testid="sim-section-relations">
        <h2 className="text-lg font-semibold text-[#0D1F3C] mb-4">Matrice di Similarita & Diagramma Relazionale</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border-[#0D1F3C]/8">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-[#0D1F3C] mb-3">Heatmap Similarita</h3>
              <SimilarityHeatmap participants={participants} matrix={matrix} />
            </CardContent>
          </Card>
          <Card className="border-[#0D1F3C]/8">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-[#0D1F3C] mb-3">Grafo Relazionale</h3>
              <RelationalDiagram participants={participants} matrix={matrix} />
              <p className="text-xs text-[#0D1F3C]/50 mt-2 text-center">
                Spessore linea proporzionale alla similarita
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 5. Dispersione per area */}
      <section data-testid="sim-section-dispersion">
        <h2 className="text-lg font-semibold text-[#0D1F3C] mb-4">Dispersione per Area</h2>
        <Card className="border-[#0D1F3C]/8">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {Object.entries(dispersion).map(([area, stats]) => (
                <div key={area} className="p-3 rounded-lg bg-[#F5F6FA]">
                  <div className="text-xs text-[#0D1F3C]/60 font-medium">{area}</div>
                  <div className="text-lg font-bold" style={{ color: bandColor(stats.avg) }}>
                    {stats.avg}
                  </div>
                  <div className="text-[10px] text-[#0D1F3C]/50">σ {stats.std}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 6-7-8. AI sections */}
      {sim.report_html && (
        <section
          data-testid="sim-section-ai"
          className="prose prose-sm max-w-none [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-[#0D1F3C] [&_h2]:mt-6 [&_h2]:mb-3 [&_p]:text-[#0D1F3C]/80 [&_p]:leading-relaxed [&_ul]:text-[#0D1F3C]/80"
          dangerouslySetInnerHTML={{ __html: sim.report_html }}
        />
      )}

      {/* 9. Disclaimer */}
      <section data-testid="sim-section-disclaimer" className="text-xs text-[#0D1F3C]/50 italic border-t border-[#0D1F3C]/8 pt-4">
        I risultati prodotti da Selly hanno finalita orientativa e di supporto decisionale. Non si sostituiscono al giudizio professionale del responsabile delle risorse umane. Selly supporta — non sostituisce — la valutazione umana.
      </section>
    </div>
  );
}
